import * as React from 'react';
import moment from 'moment-timezone';

import {
  Checkbox,
  CheckboxVisibility,
  DetailsList,
  IColumn,
  Link,
  Persona,
  PersonaSize,
  SearchBox,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Toggle,
} from '@fluentui/react';

import { ROUTES } from '../../config/routes';
import { SearchPatient } from '../../redux/patients/types';
import { PageHeader } from '../core/PageHeader';
import { InfoMessage } from '../core/InfoMessage';
import { patientSearchChanged } from '../../redux/patients/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/types';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDebounce, useQueryString } from '../../util/hooks';
import { formatPhoneNumber } from '../../util/PhoneNumber';

export const PatientsSearchPage = () => {
  const [columns] = useState<IColumn[]>(buildColumns());
  const [searchText, setSearchText] = useQueryString('q', '');
  const debouncedSearchText = useDebounce(searchText, 500);

  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    patients: { isSearching, patients },
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    dispatch(patientSearchChanged(debouncedSearchText));
  }, [dispatch, debouncedSearchText]);

  const renderStatus = () => {
    if (isSearching) {
      return <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Searching for you..." />;
    }

    if (searchText && patients && patients.length === 0) {
      return (
        <MessageBar messageBarType={MessageBarType.info} isMultiline>
          There are no results, please refine your search.
          <div>Search for any patient by name, email, chart ID, date of birth (MM/DD/YYYY), or phone number (5551234567)</div>
        </MessageBar>
      );
    } else if (!patients || patients.length === 0) {
      return (
        <InfoMessage>
          <div>Type your search criteria above.</div>
        </InfoMessage>
      );
    }
    return null;
  };

  const onActiveItemChanged = (item: SearchPatient) => {
    navigate(`${ROUTES.patients}/${item.patientId}`);
  };

  function buildColumns(): IColumn[] {
    return [
      {
        key: 'patient',
        name: 'Patient',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: SearchPatient) => {
          const onRenderPrimaryText = () => {
            return <Link onClick={() => navigate(`${ROUTES.patients}/${item.patientId}`)}>{item.fullName}</Link>;
          };
          return <Persona size={PersonaSize.size24} text={item.fullName} onRenderPrimaryText={onRenderPrimaryText} imageUrl={item.avatarUrl} />;
        },
      },
      {
        key: 'dateOfBirth',
        name: 'Date of Birth',
        fieldName: 'dateOfBirth',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: SearchPatient) => {
          return item.dateOfBirth ? moment(item.dateOfBirth).format('MM/DD/YYYY') : '';
        },
      },
      {
        key: 'email',
        name: 'Patient Email',
        fieldName: 'email',
        minWidth: 120,
        maxWidth: 240,
      },
      {
        key: 'phone',
        name: 'Phone',
        fieldName: 'phone',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: SearchPatient) => {
          return formatPhoneNumber(item.phone);
        },
      },
      {
        key: 'mrn',
        name: 'MRN',
        fieldName: 'mrn',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        key: 'mainPatientOfAccount',
        name: 'Main patient',
        fieldName: 'mainPatientOfAccount',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: SearchPatient) => {
          return <Checkbox disabled checked={item.mainPatientOfAccount} />;
        },
      },
      {
        key: 'status',
        name: 'Active',
        fieldName: 'status',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: SearchPatient) => {
          return <Checkbox disabled checked={item.status === 'ACTIVE'} />;
        },
      },
      {
        key: 'userAccountEmail',
        name: 'Account Email',
        fieldName: 'userAccountEmail',
        minWidth: 120,
        maxWidth: 240,
      },
    ];
  }

  return (
    <>
      <PageHeader title="Patient Search" />
      <div className="flex">
        <SearchBox
          className="flex-grow"
          value={searchText}
          placeholder="Search by name, email@heal.com, date of birth (MM/DD/YYYY), phone (5551234567), MRN or chart ID"
          onChange={(ev, v) => setSearchText(v ?? '')}
          data-tid="inp_search"
        />
        <div style={{ marginLeft: 10 }}>
          <Toggle
            className="flex flex-center"
            style={{ margin: 0 }}
            label="Only active&nbsp;"
            checked={showActiveOnly}
            onChange={(e, checked) => checked !== undefined && setShowActiveOnly(checked)}
            data-tid="btn_onlyActive"
          />
        </div>
      </div>
      <DetailsList
        checkboxVisibility={CheckboxVisibility.hidden}
        items={(patients || []).filter((p) => (showActiveOnly ? p.status === 'ACTIVE' : p))}
        setKey="set"
        columns={columns}
        onActiveItemChanged={onActiveItemChanged}
      />
      {renderStatus()}
    </>
  );
};
